/**
 * 文件下载工具函数
 */
import { ExportFormat } from '../types/export'
import { getTimestampedFilename } from './time'

/**
 * 下载文件到本地
 */
export function downloadFile(content: string, filename: string, mimeType = 'text/plain'): void {
  const blob = new Blob([content], { type: mimeType })
  downloadBlob(blob, filename)
}

/**
 * 下载 Blob 文件
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  URL.revokeObjectURL(url)
}

/**
 * 下载文本文件
 */
export function downloadText(content: string, filename: string): void {
  downloadFile(content, filename.endsWith('.txt') ? filename : `${filename}.txt`, 'text/plain')
}

/**
 * 下载 HTML 文件
 */
export function downloadHTML(content: string, filename: string): void {
  downloadFile(content, filename.endsWith('.html') ? filename : `${filename}.html`, 'text/html')
}

/**
 * 下载 Markdown 文件
 */
export function downloadMarkdown(content: string, filename: string): void {
  downloadFile(content, filename.endsWith('.md') ? filename : `${filename}.md`, 'text/markdown')
}

/**
 * 下载 JSON 文件
 */
export function downloadJSON(data: any, filename: string): void {
  const content = JSON.stringify(data, null, 2)
  downloadFile(content, filename.endsWith('.json') ? filename : `${filename}.json`, 'application/json')
}

/**
 * 下载 CSV 文件
 */
export function downloadCSV(data: string[][], filename: string): void {
  const content = data.map(row => 
    row.map(field => {
      // 如果字段包含逗号或引号，需要用引号包围
      if (field.includes(',') || field.includes('"')) {
        return `"${field.replace(/"/g, '""')}"`
      }
      return field
    }).join(',')
  ).join('\n')
  
  downloadFile(content, filename.endsWith('.csv') ? filename : `${filename}.csv`, 'text/csv')
}

/**
 * 下载图片文件
 */
export function downloadImage(canvas: HTMLCanvasElement, filename: string, format: 'png' | 'jpeg' = 'png', quality = 0.9): void {
  const mimeType = format === 'png' ? 'image/png' : 'image/jpeg'
  canvas.toBlob((blob) => {
    if (blob) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename.endsWith(`.${format}`) ? filename : `${filename}.${format}`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }
  }, mimeType, quality)
}

/**
 * 根据格式下载文件
 */
export function downloadByFormat(
  content: string,
  filename: string,
  format: ExportFormat,
  options?: {
    timestamp?: boolean
    quality?: number
    canvas?: HTMLCanvasElement
  }
): void {
  const baseFilename = options?.timestamp ? getTimestampedFilename(filename, format) : filename
  
  switch (format) {
    case 'markdown':
      downloadMarkdown(content, baseFilename)
      break
    case 'html':
      downloadHTML(content, baseFilename)
      break
    case 'pdf':
      // PDF 导出需要特殊处理，这里先下载 HTML
      console.warn('PDF export requires special handling, downloading HTML instead')
      downloadHTML(content, baseFilename + '.html')
      break
    case 'word':
      // Word 导出需要特殊处理，这里先下载 HTML
      console.warn('Word export requires special handling, downloading HTML instead')
      downloadHTML(content, baseFilename + '.html')
      break
    case 'png':
    case 'jpeg':
      if (options?.canvas) {
        downloadImage(options.canvas, baseFilename, format, options.quality)
      } else {
        console.error('Canvas element is required for image export')
      }
      break
    case 'csv':
      downloadCSV(content.split('\n').map(line => [line]), baseFilename)
      break
    case 'excel':
      // Excel 导出需要特殊处理，这里先下载 CSV
      console.warn('Excel export requires special handling, downloading CSV instead')
      downloadCSV(content.split('\n').map(line => [line]), baseFilename + '.csv')
      break
    case 'rtf':
      downloadFile(content, baseFilename + '.rtf', 'application/rtf')
      break
    case 'svg':
      downloadFile(content, baseFilename + '.svg', 'image/svg+xml')
      break
    default:
      downloadText(content, baseFilename)
  }
}

/**
 * 复制文本到剪贴板
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // 使用现代 Clipboard API
      await navigator.clipboard.writeText(text)
      return true
    } else {
      // 降级方案
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      const result = document.execCommand('copy')
      document.body.removeChild(textArea)
      return result
    }
  } catch (error) {
    console.error('Failed to copy text to clipboard:', error)
    return false
  }
}

/**
 * 下载文件流（用于从服务器下载文件）
 */
export async function downloadFileFromUrl(
  url: string,
  filename: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  try {
    const response = await fetch(url)
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    const contentLength = response.headers.get('content-length')
    const reader = response.body?.getReader()
    const chunks: BlobPart[] = []
    let receivedLength = 0
    
    if (reader) {
      while (true) {
        const { done, value } = await reader.read()
        
        if (done) break
        
        chunks.push(value)
        receivedLength += value.length
        
        // 更新进度
        if (onProgress && contentLength) {
          const progress = (receivedLength / parseInt(contentLength)) * 100
          onProgress(progress)
        }
      }
    }
    
    // 合并所有块
    const blob = new Blob(chunks, { type: 'application/octet-stream' })
    createDownloadLink(blob, filename)
    
  } catch (error) {
    console.error('Failed to download file from URL:', error)
    throw error
  }
}

/**
 * 创建下载链接（用于大文件或流式下载）
 */
export function createDownloadLink(blob: Blob, filename: string): string {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  
  // 延迟清理 URL，确保下载开始
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, 100)
  
  return url
}